/**
 * Client types + fetch helper for `/api/premium-invite-config`.
 * (Mirror of server payload in `api/lib/premiumInviteConfigRepository.ts`.)
 */

export type ReelsPlayerVariant = "use_posthog" | "classic" | "three_teaser";

export type ReelsPlaybackSource = "mux_api" | "curated";

/** Coincide con límite en `api/lib/premiumInviteReelsPlaylist.ts`. */
export const PREMIUM_INVITE_MAX_CURATED_REELS_IDS = 40;

/** localStorage key: admin escribe esto al guardar → otras tabs re-fetchan la config al instante. */
export const PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY = "premium_invite_config_updated";

/**
 * Notifica a pestañas del mismo origen que `premium_invite_config` cambió.
 * - `storage` event → otras tabs
 * - `CustomEvent` → misma tab (landings DualCTA abiertas en otro panel no aplican;
 *   pero sí listeners en el mismo window + pattern alineado con siteSettingsSync)
 */
export function broadcastPremiumInviteConfigUpdated(): void {
  if (typeof window === "undefined") return;
  // Drop client memory so next soft fetch hits network with fresh admin data.
  memCfg = null;
  inflight = null;
  try {
    localStorage.setItem(PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY, String(Date.now()));
  } catch {
    /* private mode / quota */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY, {
        detail: { at: Date.now() },
      }),
    );
  } catch {
    /* ignore */
  }
}

export interface PremiumInvitePublicConfig {
  experiment_enabled: boolean;
  force_layout: "premium_simple" | "reels_feed" | null;
  use_posthog_layout: boolean;
  reels_rollout_percent: number;
  reels_teaser_count: number;
  reels_player_variant: ReelsPlayerVariant;
  reels_playback_source: ReelsPlaybackSource;
  reels_curated_playback_ids: string[];
  free_telegram_url: string | null;
  /** Override por país de la URL de Telegram gratis. { ISO2: "https://t.me/..." } */
  free_telegram_url_by_country: Record<string, string> | null;
  /** Toggle maestro del "Elemento free-telegram". false = ocultar el free-telegram en TODAS
   *  las superficies. null/true = habilitado. Argentina siempre excluida (va directo a Stripe). */
  free_telegram_enabled: boolean | null;
  vip_checkout_base_url: string | null;
  end_screen_headline: string | null;
  end_screen_free_cta_label: string | null;
  end_screen_vip_cta_label: string | null;
  /** null = heredar `VITE_MUX_CTA_FRIENDLY_BANNER` en Railway. */
  mux_cta_friendly_banner: boolean | null;
  /** null = heredar `VITE_MUX_CTA_FREE_JOIN_COPY` en Railway. */
  mux_cta_free_join_copy: boolean | null;
  friendly_vip_badge_text: string | null;
  friendly_vip_disclaimer_text: string | null;
  simple_show_telegram_card: boolean | null;
  simple_show_vip_card: boolean | null;
  /** DualCTAVideoLanding: false = oculta el botón "Canal gratis" (Telegram). null/true = mostrar si hay URL. */
  dual_cta_free_telegram_enabled: boolean | null;
  /** true = card visible pero gris, sin checkout; se registran taps en PostHog (`bio_simple_locked_card_interaction`). */
  simple_telegram_card_locked: boolean;
  /** Igual que `simple_telegram_card_locked` para la card Mi VIP. */
  simple_vip_card_locked: boolean;
  /** Override de texto del título de la card Telegram (vacío = usar traducción por idioma). */
  simple_telegram_card_title: string | null;
  /** Override del CTA de la card Telegram. */
  simple_telegram_card_cta: string | null;
  /** Override del label del botón "Canal gratis" en DualCTAVideoLanding. */
  dual_cta_free_channel_label: string | null;
  /** Override del título de la card Mi VIP. */
  simple_vip_card_title: string | null;
  /** Override del subtítulo de la card Mi VIP. */
  simple_vip_card_subtitle: string | null;
  /** Override del CTA de la card Mi VIP. */
  simple_vip_card_cta: string | null;
  /** Override del badge pequeño encima del título hero. */
  simple_hero_badge: string | null;
  /** Override del título principal del hero. */
  simple_hero_title: string | null;
  /** Override del subtítulo del hero. */
  simple_hero_subtitle: string | null;
  /** Override de la línea de confianza. */
  simple_hero_trust_line: string | null;
  /** Override del pill de valor/ruta. */
  simple_hero_path_line: string | null;
  /** Override del CTA del enlace Telegram gratis. */
  simple_free_telegram_cta: string | null;
  /** Override del badge en el enlace Telegram gratis. */
  simple_free_telegram_badge: string | null;
  /** Override del intro encima del enlace Telegram gratis. */
  simple_free_telegram_intro: string | null;
  /** Override de la sublinea debajo del enlace Telegram gratis. */
  simple_free_telegram_subline: string | null;
  /** Override del chip "Recomendado" encima de la card VIP. */
  simple_vip_recommended_badge: string | null;
  /** Override del texto del botón en el video Mux. */
  simple_membership_video_cta: string | null;
  /** Override del hint para tocar el video. */
  simple_vip_tap_hint: string | null;
  /** Override del hint debajo del botón Telegram. */
  simple_telegram_activation_hint: string | null;
  /** Override de la línea del footer. */
  simple_footer_trust_line: string | null;
  /** JSON: { "en": { badge, heroTitle, ... }, "pt": {...}, "fr": {...}, "it": {...} }. */
  simple_texts_by_lang: string | null;
  /** true = muestra overlay MuxVideoPlayerWithCTA fullscreen sobre el template, con botones VIP + Free. */
  simple_mux_overlay_enabled: boolean;
  /** Mux playback ID para el overlay (null = usa el video de membresía ya configurado). */
  simple_mux_overlay_playback_id: string | null;
  /** Precio VIP en USD (null = heredar VITE_PREMIUM_USD_VIP_PRICE / default 49.99). Editable desde admin sin deploy. */
  vip_price_usd: number | null;
  /** Precio VIP en ARS (null = heredar VITE_PREMIUM_ARS_VIP_PRICE / default 49999). */
  vip_price_ars: number | null;
  /** Precio Standard en USD (null = heredar VITE_PREMIUM_USD_STANDARD_PRICE / default 29.99). */
  standard_price_usd: number | null;
  /** Precio Standard en ARS (null = heredar VITE_PREMIUM_ARS_STANDARD_PRICE / default 29999). */
  standard_price_ars: number | null;
  /** Monto del plan Telegram Basic en centavos USD (null = heredar VITE_TELEGRAM_BASIC_USD_AMOUNT_CENTS / default 699). */
  telegram_basic_price_cents: number | null;
  /** Precio Standard ARS para /ar/invitacion (null = default 29999). */
  ar_invitacion_standard_price_ars: number | null;
  /** Precio VIP ARS para /ar/invitacion (null = default 49999). */
  ar_invitacion_vip_price_ars: number | null;
  /** Monto sugerido de la donación VIP internacional en USD (null = default 300). */
  vip_donation_suggested_usd: number | null;
  /** Monto mínimo del regalo Sorprendeme en USD (null = default 50). */
  sorprendeme_min_amount: number | null;
  /** JSON de tiers de monto Sorprendeme: [{amount,label,emoji?}]. null = default en código. */
  sorprendeme_amount_tiers: string | null;
  /** true = CTA secundario "Acceso gratis" en /invitacion-exclusiva (registro Clerk → aprobación manual). null/false = oculto. */
  free_access_cta_enabled: boolean | null;
  /** Label del CTA "Acceso gratis" (null = default localizado). */
  free_access_cta_label: string | null;
  updated_at: string | null;
}

const SAFE_DEFAULT: PremiumInvitePublicConfig = {
  experiment_enabled: true,
  force_layout: null,
  use_posthog_layout: true,
  reels_rollout_percent: 0,
  reels_teaser_count: 3,
  reels_player_variant: "three_teaser",
  reels_playback_source: "mux_api",
  reels_curated_playback_ids: [],
  free_telegram_url: null,
  free_telegram_url_by_country: null,
  free_telegram_enabled: null,
  vip_checkout_base_url: null,
  end_screen_headline: null,
  end_screen_free_cta_label: null,
  end_screen_vip_cta_label: null,
  mux_cta_friendly_banner: null,
  mux_cta_free_join_copy: null,
  friendly_vip_badge_text: null,
  friendly_vip_disclaimer_text: null,
  simple_show_telegram_card: null,
  simple_show_vip_card: null,
  dual_cta_free_telegram_enabled: null,
  simple_telegram_card_locked: false,
  simple_vip_card_locked: false,
  simple_telegram_card_title: null,
  simple_telegram_card_cta: null,
  dual_cta_free_channel_label: null,
  simple_vip_card_title: null,
  simple_vip_card_subtitle: null,
  simple_vip_card_cta: null,
  simple_hero_badge: null,
  simple_hero_title: null,
  simple_hero_subtitle: null,
  simple_hero_trust_line: null,
  simple_hero_path_line: null,
  simple_free_telegram_cta: null,
  simple_free_telegram_badge: null,
  simple_free_telegram_intro: null,
  simple_free_telegram_subline: null,
  simple_vip_recommended_badge: null,
  simple_membership_video_cta: null,
  simple_vip_tap_hint: null,
  simple_telegram_activation_hint: null,
  simple_footer_trust_line: null,
  simple_texts_by_lang: null,
  simple_mux_overlay_enabled: false,
  simple_mux_overlay_playback_id: null,
  vip_price_usd: null,
  vip_price_ars: null,
  standard_price_usd: null,
  standard_price_ars: null,
  telegram_basic_price_cents: null,
  ar_invitacion_standard_price_ars: null,
  ar_invitacion_vip_price_ars: null,
  vip_donation_suggested_usd: null,
  sorprendeme_min_amount: null,
  sorprendeme_amount_tiers: null,
  free_access_cta_enabled: null,
  free_access_cta_label: null,
  updated_at: null,
};

function clampPct(n: number): number {
  return Math.min(100, Math.max(0, Math.round(n)));
}

function clampTeaser(n: number): number {
  return Math.min(20, Math.max(1, Math.round(n)));
}

function stripStr(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function coalesceBool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

function coalesceTriBool(v: unknown): boolean | null {
  if (v === true) return true;
  if (v === false) return false;
  return null;
}

function coalesceFreeTelegramUrlByCountry(v: unknown): Record<string, string> | null {
  if (v === null || v === undefined) return null;
  let obj: unknown = v;
  if (typeof v === "string") {
    if (!v.trim()) return null;
    try {
      obj = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const out: Record<string, string> = {};
  for (const [k, raw] of Object.entries(obj as Record<string, unknown>)) {
    const code = String(k).trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const val = typeof raw === "string" ? raw.trim() : "";
    if (val) out[code] = val;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function coalescePlayerVariant(v: unknown): ReelsPlayerVariant {
  if (v === "classic" || v === "three_teaser" || v === "use_posthog") return v;
  return SAFE_DEFAULT.reels_player_variant;
}

const MUX_PLAYBACK_ID_RE = /^[a-zA-Z0-9][a-zA-Z0-9_-]{3,79}$/;

/** Misma regla que el backend: orden preservado, dedupe, tope. */
export function parseMuxPlaybackIdsFromEditorText(text: string): string[] {
  const parts = text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of parts) {
    if (!MUX_PLAYBACK_ID_RE.test(x)) continue;
    if (seen.has(x)) continue;
    seen.add(x);
    out.push(x);
    if (out.length >= PREMIUM_INVITE_MAX_CURATED_REELS_IDS) break;
  }
  return out;
}

/** Resumen para el editor admin (avisos de tokens inválidos / duplicados). */
export function summarizeCuratedPlaybackEditor(text: string): {
  validIds: string[];
  invalidTokenCount: number;
  duplicateTokenCount: number;
} {
  const raw = text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const validIds: string[] = [];
  const seen = new Set<string>();
  let invalidTokenCount = 0;
  let duplicateTokenCount = 0;
  for (const x of raw) {
    if (!MUX_PLAYBACK_ID_RE.test(x)) {
      if (x.length > 0) invalidTokenCount += 1;
      continue;
    }
    if (seen.has(x)) {
      duplicateTokenCount += 1;
      continue;
    }
    seen.add(x);
    validIds.push(x);
    if (validIds.length >= PREMIUM_INVITE_MAX_CURATED_REELS_IDS) break;
  }
  return { validIds, invalidTokenCount, duplicateTokenCount };
}

function coalesceCuratedIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of raw) {
    const s = String(x ?? "").trim();
    if (!MUX_PLAYBACK_ID_RE.test(s)) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= PREMIUM_INVITE_MAX_CURATED_REELS_IDS) break;
  }
  return out;
}

function coalescePlaybackSource(v: unknown): ReelsPlaybackSource {
  return v === "curated" ? "curated" : "mux_api";
}

/**
 * Normalizes an API/config object (public GET body or `config` from admin).
 * Returns null only if `data` is not a plain object (e.g. array, wrong JSON root).
 */
export function parsePremiumInvitePublicConfig(data: unknown): PremiumInvitePublicConfig | null {
  if (data === null || typeof data !== "object" || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;

  const fl = o.force_layout;
  const force_layout =
    fl === "premium_simple" || fl === "reels_feed"
      ? fl
      : null;

  const rpRaw = o.reels_rollout_percent;
  const rp =
    typeof rpRaw === "number"
      ? rpRaw
      : typeof rpRaw === "string"
        ? Number(rpRaw)
        : NaN;
  const reels_rollout_percent = Number.isFinite(rp) ? clampPct(rp) : SAFE_DEFAULT.reels_rollout_percent;

  const tcRaw = o.reels_teaser_count;
  const tc =
    typeof tcRaw === "number" ? tcRaw : typeof tcRaw === "string" ? Number(tcRaw) : NaN;
  const reels_teaser_count = Number.isFinite(tc) ? clampTeaser(tc) : SAFE_DEFAULT.reels_teaser_count;

  const reels_player_variant = coalescePlayerVariant(o.reels_player_variant);

  let reels_playback_source = coalescePlaybackSource(o.reels_playback_source);
  const reels_curated_playback_ids = coalesceCuratedIds(o.reels_curated_playback_ids);
  if (reels_playback_source === "curated" && reels_curated_playback_ids.length === 0) {
    reels_playback_source = "mux_api";
  }

  const updatedRaw = o.updated_at;
  const updated_at = typeof updatedRaw === "string" ? updatedRaw : null;

  return {
    experiment_enabled: coalesceBool(o.experiment_enabled, SAFE_DEFAULT.experiment_enabled),
    force_layout,
    use_posthog_layout: coalesceBool(o.use_posthog_layout, SAFE_DEFAULT.use_posthog_layout),
    reels_rollout_percent,
    reels_teaser_count,
    reels_player_variant,
    reels_playback_source,
    reels_curated_playback_ids:
      reels_playback_source === "curated" ? reels_curated_playback_ids : [],
    free_telegram_url: stripStr(o.free_telegram_url),
    free_telegram_url_by_country: coalesceFreeTelegramUrlByCountry(o.free_telegram_url_by_country),
    free_telegram_enabled: coalesceTriBool(o.free_telegram_enabled),
    vip_checkout_base_url: stripStr(o.vip_checkout_base_url),
    end_screen_headline: stripStr(o.end_screen_headline),
    end_screen_free_cta_label: stripStr(o.end_screen_free_cta_label),
    end_screen_vip_cta_label: stripStr(o.end_screen_vip_cta_label),
    mux_cta_friendly_banner: coalesceTriBool(o.mux_cta_friendly_banner),
    mux_cta_free_join_copy: coalesceTriBool(o.mux_cta_free_join_copy),
    friendly_vip_badge_text: stripStr(o.friendly_vip_badge_text),
    friendly_vip_disclaimer_text: stripStr(o.friendly_vip_disclaimer_text),
    simple_show_telegram_card: coalesceTriBool(o.simple_show_telegram_card),
    simple_show_vip_card: coalesceTriBool(o.simple_show_vip_card),
    dual_cta_free_telegram_enabled: coalesceTriBool(o.dual_cta_free_telegram_enabled),
    simple_telegram_card_locked: coalesceBool(
      o.simple_telegram_card_locked,
      SAFE_DEFAULT.simple_telegram_card_locked,
    ),
    simple_vip_card_locked: coalesceBool(
      o.simple_vip_card_locked,
      SAFE_DEFAULT.simple_vip_card_locked,
    ),
    simple_telegram_card_title: stripStr(o.simple_telegram_card_title),
    simple_telegram_card_cta: stripStr(o.simple_telegram_card_cta),
    dual_cta_free_channel_label: stripStr(o.dual_cta_free_channel_label),
    simple_vip_card_title: stripStr(o.simple_vip_card_title),
    simple_vip_card_subtitle: stripStr(o.simple_vip_card_subtitle),
    simple_vip_card_cta: stripStr(o.simple_vip_card_cta),
    simple_hero_badge: stripStr(o.simple_hero_badge),
    simple_hero_title: stripStr(o.simple_hero_title),
    simple_hero_subtitle: stripStr(o.simple_hero_subtitle),
    simple_hero_trust_line: stripStr(o.simple_hero_trust_line),
    simple_hero_path_line: stripStr(o.simple_hero_path_line),
    simple_free_telegram_cta: stripStr(o.simple_free_telegram_cta),
    simple_free_telegram_badge: stripStr(o.simple_free_telegram_badge),
    simple_free_telegram_intro: stripStr(o.simple_free_telegram_intro),
    simple_free_telegram_subline: stripStr(o.simple_free_telegram_subline),
    simple_vip_recommended_badge: stripStr(o.simple_vip_recommended_badge),
    simple_membership_video_cta: stripStr(o.simple_membership_video_cta),
    simple_vip_tap_hint: stripStr(o.simple_vip_tap_hint),
    simple_telegram_activation_hint: stripStr(o.simple_telegram_activation_hint),
    simple_footer_trust_line: stripStr(o.simple_footer_trust_line),
    simple_texts_by_lang: typeof o.simple_texts_by_lang === "string" ? o.simple_texts_by_lang : null,
    simple_mux_overlay_enabled: coalesceBool(o.simple_mux_overlay_enabled, SAFE_DEFAULT.simple_mux_overlay_enabled),
    simple_mux_overlay_playback_id: stripStr(o.simple_mux_overlay_playback_id),
    vip_price_usd: (() => { const n = Number(o.vip_price_usd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    vip_price_ars: (() => { const n = Number(o.vip_price_ars); return Number.isFinite(n) && n > 0 ? n : null; })(),
    standard_price_usd: (() => { const n = Number(o.standard_price_usd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    standard_price_ars: (() => { const n = Number(o.standard_price_ars); return Number.isFinite(n) && n > 0 ? n : null; })(),
    telegram_basic_price_cents: (() => { const n = Number(o.telegram_basic_price_cents); return Number.isFinite(n) && n > 0 ? n : null; })(),
    ar_invitacion_standard_price_ars: (() => { const n = Number(o.ar_invitacion_standard_price_ars); return Number.isFinite(n) && n > 0 ? n : null; })(),
    ar_invitacion_vip_price_ars: (() => { const n = Number(o.ar_invitacion_vip_price_ars); return Number.isFinite(n) && n > 0 ? n : null; })(),
    vip_donation_suggested_usd: (() => { const n = Number(o.vip_donation_suggested_usd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    sorprendeme_min_amount: (() => { const n = Number(o.sorprendeme_min_amount); return Number.isFinite(n) && n > 0 ? n : null; })(),
    sorprendeme_amount_tiers: typeof o.sorprendeme_amount_tiers === "string" ? o.sorprendeme_amount_tiers : null,
    free_access_cta_enabled: coalesceTriBool(o.free_access_cta_enabled),
    free_access_cta_label: stripStr(o.free_access_cta_label),
    updated_at,
  };
}

/** Client memory + single-flight (DualCTA + Invitacion both call this). */
const CLIENT_TTL_MS = 45_000;
let memCfg: { at: number; cfg: PremiumInvitePublicConfig } | null = null;
let inflight: Promise<PremiumInvitePublicConfig | null> | null = null;

export async function fetchPremiumInvitePublicConfig(opts?: {
  /** true = allow warm cache / browser cache (default). false = bust for admin. */
  soft?: boolean;
}): Promise<PremiumInvitePublicConfig | null> {
  const soft = opts?.soft !== false;
  if (soft && memCfg && Date.now() - memCfg.at < CLIENT_TTL_MS) {
    return memCfg.cfg;
  }
  if (soft && inflight) return inflight;

  const run = async (): Promise<PremiumInvitePublicConfig | null> => {
    try {
      const url = soft
        ? "/api/premium-invite-config"
        : `/api/premium-invite-config?ts=${Date.now()}`;
      const r = await fetch(url, {
        cache: soft ? "default" : "no-store",
        headers: soft
          ? undefined
          : {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
      });
      if (!r.ok) return null;
      const raw: unknown = await r.json();
      const cfg = parsePremiumInvitePublicConfig(raw);
      if (cfg) memCfg = { at: Date.now(), cfg };
      return cfg;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  };

  inflight = run();
  return inflight;
}

/** Call after admin save of premium-invite config. */
export function invalidatePremiumInvitePublicConfigClient(): void {
  memCfg = null;
  inflight = null;
}
